// Page views for Campfire, the Pendo replacement (UIUX-4595). Recruit renders
// the events URL, a signed token and campfire-usage into the head
// (layouts/_campfire); with any of them missing, every call here does nothing.
//
// Views are sent with navigator.sendBeacon, which outlives the page, so a view
// is not lost when the tab closes. sendBeacon cannot set headers, so the token
// goes in the query string.

const FLUSH_MS = 10_000;
const MAX_EVENTS_PER_SEND = 50;
// Refresh the token when less than this is left. Campfire refuses an expired one.
const REFRESH_MARGIN_SECONDS = 10 * 60;
const TOKEN_PATH = '/api/v4/campfire/token';

interface PageView {
  id: string;
  kind: 'page_view';
  product: string;
  path: string;
  occurred_at: string;
}

const PRODUCT_RULES: [RegExp, string][] = [
  [/^\/(admin\/)?onboard(\/|$)/, 'onboard'],
  // Agentic Engage is TalentMatch and CandidateSourcing only. Admin Engage is Recruit.
  [/^\/(admin\/)?(talent_match|candidate_sourcing)(\/|$)/, 'agentic_engage']
];

// An empty content attribute counts as missing.
const meta = (name: string): string | null => {
  const content = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content;
  return content === undefined || content === '' ? null : content;
};

let queue: PageView[] = [];
let token: string | null = null;
let eventsUrl: string | null = null;
let timer: number | undefined;

// randomUUID needs a secure context and a recent browser; fall back to 16 random bytes.
const newId = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
        b.toString(16).padStart(2, '0')
      ).join('');

export const productForPath = (path: string): string =>
  PRODUCT_RULES.find(([pattern]) => pattern.test(path))?.[1] ?? 'recruit';

const expiresAt = (jwt: string): number => {
  try {
    const body = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return (JSON.parse(atob(body)) as { exp: number }).exp;
  } catch {
    return 0;
  }
};

const refreshToken = async (): Promise<void> => {
  if (!token || expiresAt(token) - Date.now() / 1000 > REFRESH_MARGIN_SECONDS) return;
  try {
    const response = await fetch(TOKEN_PATH, { credentials: 'same-origin' });
    if (response.status === 200) token = ((await response.json()) as { token: string }).token;
  } catch {
    // Keep the current token; the next flush tries again.
  }
};

export const flush = (): void => {
  if (!token || !eventsUrl || queue.length === 0) return;
  const url = `${eventsUrl}?t=${encodeURIComponent(token)}`;
  while (queue.length > 0) {
    const events = queue.splice(0, MAX_EVENTS_PER_SEND);
    navigator.sendBeacon(url, new Blob([JSON.stringify({ events })], { type: 'text/plain' }));
  }
};

/** Records one page view. Query strings and fragments are dropped: they can hold search terms or names. */
export const recordPageView = (rawPath: string): void => {
  if (!token || !eventsUrl) return;
  const path = rawPath.split(/[?#]/)[0];
  queue.push({
    id: newId(),
    kind: 'page_view',
    product: productForPath(path),
    path,
    occurred_at: new Date().toISOString()
  });
};

/** The newest token: the page's, or a refreshed one while page views run. */
export const currentCampfireToken = (): string | null => token ?? meta('campfire-token');

export const startCampfireUsage = (): void => {
  token = meta('campfire-token');
  eventsUrl = meta('campfire-events-url');
  if (
    meta('campfire-usage') !== 'on' ||
    !token ||
    !eventsUrl ||
    typeof navigator.sendBeacon !== 'function'
  ) {
    token = null;
    return;
  }

  recordPageView(window.location.pathname);
  window.clearInterval(timer);
  timer = window.setInterval(() => {
    refreshToken()
      .then(flush)
      .catch(() => undefined);
  }, FLUSH_MS);
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
};

/** Test-only reset of module state. */
export const resetCampfireUsageForTests = (): void => {
  queue = [];
  token = null;
  eventsUrl = null;
  window.clearInterval(timer);
};
